import { Button, MenuItem } from '@mui/material';
import { useField, Field, useFormikContext } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { fontSizeValueSchema, hexColorValidationSchema, useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, Text } from '../../types/form-fields';
import { CheckboxField, ColorPickerField, FormRow, InputField } from '../fields';
import { FormBlock } from './common';
import { TextVariables } from './text-variables';

export const isTextField = (field: FormField): field is Text => {
  return field.type === FieldType.Text;
};

export const defaultTextFieldProps: Text = {
  type: FieldType.Text,
  value: 'My text',
  fontSize: 16,
  color: '#fff',
  fontWeight: 400,
  asHTML: false,
};

type TextFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');

const TextFieldSettings: FC<TextFieldSettingsProps> = ({ name, remove }) => {
  const [field] = useField<Text>(name);
  const [, , fieldValueHelpers] = useField<string>(`${name}.value`);
  const { t } = useTranslation();
  const { submitForm } = useFormikContext();

  const asHTML = field.value?.asHTML;

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  return (
    <TextFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.value`}
          component={InputField}
          label={asHTML ? t('pages.campaign.checkboxField.textHtml') : t('pages.campaign.checkboxField.text')}
          multiline
          maxRows={5}
          validate={(value: string) => validate(value, textValueSchema)}
        />

        <Field name={`${name}.asHTML`} component={CheckboxField} label={t('pages.campaign.checkboxField.useAsHtml')} />

        {field.value?.variables && (
          <TextVariables
            variables={field.value.variables}
            onVariableClick={(variable) => {
              fieldValueHelpers.setValue(`${field.value.value}${variable}`);
              submitForm();
            }}
          />
        )}
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.fontSize`}
          component={InputField}
          label={t('pages.campaign.checkboxField.fontSize')}
          type="number"
          validate={(value: string) => validate(value, fontSizeValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field name={`${name}.fontWeight`} component={InputField} label={t('pages.campaign.checkboxField.fontWeight')} select defaultValue="400">
          {[300, 400, 500, 700].map((fontWeight) => (
            <MenuItem key={fontWeight} value={fontWeight}>
              {fontWeight}
            </MenuItem>
          ))}
        </Field>
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.color`}
          component={ColorPickerField}
          label={t('pages.campaign.checkboxField.color')}
          validate={(value: string) => validate(value, hexColorValidationSchema)}
        />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </TextFieldSettingsWrapper>
  );
};

type TextFieldProps = {
  name: string;
  remove?: () => void;
};

export const TextField: FC<TextFieldProps> = ({ name, remove }) => {
  const [field] = useField<Text>(name);
  const dispatch = useAppDispatch();

  const { value, fontSize, color, fontWeight, asHTML } = field.value;

  const textContent = value;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <TextFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick}>
      <TextContent
        style={{
          fontSize: `${fontSize}px`,
          color: color,
          fontWeight,
        }}
        className="generic-field-text-content"
        dangerouslySetInnerHTML={asHTML ? { __html: textContent } : undefined}
      >
        {asHTML ? null : textContent}
      </TextContent>
    </FormBlock>
  );
};

const TextFieldSettingsWrapper = styled.div``;

const TextContent = styled.div`
  white-space: pre-line;
  line-height: 1.2;
`;
